import { ScopeContext, useScope } from 'dreamstate';
import { useCallback } from 'react';

import { Callable } from '@vimeo/core/types/generic';

import { ELiveSignal } from '../../store/signals';
import { IConfirmationNotification, ISimpleNotification } from '../../types/notifications';

export type TNotificationHandler = (notification: ISimpleNotification) => void;

export type TConfirmationControls = [(notification: IConfirmationNotification) => void, Callable];

/**
 * Use memoized hooks that allow sending confirmation and cancel request when it is needed.
 * Allows to reduce amount of required code where notification sending is required.
 */
export function useBroadcasterActionConfirmation(): TConfirmationControls {
  const scope: ScopeContext = useScope();

  const askConfirmation = useCallback(
    (notification: IConfirmationNotification) => {
      scope.emitSignal({
        type: ELiveSignal.CONFIRMATION_NOTIFICATION,
        data: notification,
      });
    },
    [scope],
  );

  const cancelConfirmation = useCallback(() => {
    scope.emitSignal({
      type: ELiveSignal.CONFIRMATION_NOTIFICATION_CLEAR,
    });
  }, [scope]);

  return [askConfirmation, cancelConfirmation];
}

/**
 * Use memoized hooks that allow sending notifications.
 * Allows to reduce amount of required code where notification sending is required.
 */
export function useSimpleNotification(): TNotificationHandler {
  const scope: ScopeContext = useScope();

  return useCallback(
    (notification: ISimpleNotification) => {
      scope.emitSignal({
        type: ELiveSignal.SHOW_SIMPLE_NOTIFICATION,
        data: notification,
      });
    },
    [scope],
  );
}
